import React, { useState, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { ErrorMessage, Button, Table, ErrorSummary } from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import { getApplicationDocumentId } from '../../features/application/applicationJourneySlice';
import {
  setChosenChargePoint,
  getIndividualChargepointDetails,
  getNumberOfCustomerNominatedChargepoints,
  setSocketAllocationPage,
  setIndividualChargepointDetails,
  setNumberOfChargepointsSocketsInstalled,
  setNumberOfChargepointsInstalled,
  setClaim,
  getGrantTypeSelected,
} from '../../features/installer/installerJourneySlice';
import {
  saveChargePoints,
  getApplicationClaim,
  deleteChargePoint,
  continueLater,
  getAccount,
} from '../../firebase';
import useTitle from '../../hooks/useTitle';

const ChargepointsSummary = () => {
  const history = useHistory();
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const dispatch = useDispatch();
  useTitle(
    'installer.ChargepointsSummary.s17',
    'Add details for each chargepoint installed - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const documentId = useSelector(getApplicationDocumentId);
  const individualChargepointDetailsRedux = useSelector(
    getIndividualChargepointDetails
  );
  const grantTypeSelectedRedux = useSelector(getGrantTypeSelected);

  const applicationId = useSelector(getApplicationDocumentId);
  const customerNominatedChargepointSocketsRedux = useSelector(
    getNumberOfCustomerNominatedChargepoints
  );

  const [errorMsg, setErrorMsg] = useState(null);
  const [hasSerialError, setHasSerialError] = useState(false);
  const [hasPhotoUploadError, setHasPhotoUploadError] = useState(false);
  const [dashboardLink, setDashboardLink] = useState('/dashboardHub/dashboard');
  const [errorList, setErrorList] = useState([]);

  useEffect(() => {
    getAccount()
      .then((response) => {
        const { data } = response;

        if (data?.account_type === 'Property Management Company') {
          setDashboardLink('/dashboardHub/dashboard-pmc');
        } else if (data?.account_type === 'Landlord') {
          setDashboardLink('/dashboardHub/dashboard');
        } else if (data?.account_type === 'Installer') {
          setDashboardLink('/installer/claim-grant/installer-dashboard');
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.debug('Get Account error:', err.message);
        throw new Error(err);
      });

    if (!grantTypeSelectedRedux && grantTypeSelectedRedux === '') {
      getApplicationClaim({ id: documentId })
        .then((response) => {
          const { data } = response;

          dispatch(setClaim(data));
        })
        .catch((error) => {
          if (error.code === 'functions/permission-denied') {
            window.location.assign(dashboardLink);
          }
        });
    }
  }, []);

  const handleLink = () => {
    continueLater({
      applicationId,
    })
      .then((response) => {
        if (response?.data?.success) window.location.assign(dashboardLink);
      })
      .catch(() => {
        // TODO: Show error
      });
  };

  const handleDeleteLink = async (event, chargepointIndex) => {
    event.preventDefault();

    const individualChargepointDetailsCopy = [
      ...individualChargepointDetailsRedux,
    ];
    const deletedChargepoint = individualChargepointDetailsCopy.splice(
      chargepointIndex,
      1
    );

    // pass the appClaim id and chargepoint uid
    await deleteChargePoint({
      id: documentId,
      chargepoint_uid: deletedChargepoint[0].uid,
    });
    dispatch(setIndividualChargepointDetails(individualChargepointDetailsCopy));
  };

  const handleChosenCP = (chargepointIndex) => {
    dispatch(
      setChosenChargePoint(individualChargepointDetailsRedux[chargepointIndex])
    );
  };

  const tableRows = [];
  let isSerialMissing = false;
  let isPhotoMissing = false;

  individualChargepointDetailsRedux.forEach((chargepoint, index) => {
    const { model, serialNumber, photos, socket } = chargepoint;

    if (!isSerialMissing) {
      isSerialMissing = !serialNumber || serialNumber < 4;
    }
    if (!isPhotoMissing) {
      isPhotoMissing = photos?.length < 2;
    }

    const photoArray =
      typeof photos === 'string' || photos instanceof String
        ? photos.split(',')
        : photos;

    const photoList = photoArray?.map((photo) => (
      <li style={{ listStyle: 'none' }}>{photo.fileName}</li>
    ));

    const actionArray = [
      <Link
        className="govuk-link"
        to="/installer/additional-chargepoints-details"
        onClick={() => handleChosenCP(index)}
        key={nanoid()}
      >
        <pre className="govuk-!-margin-0 govuk-link">
          {isSerialMissing || isPhotoMissing
            ? copyProvider.getCopy(
                'installer.ChargepointsSummary.s9',
                'Add details'
              )
            : copyProvider.getCopy(
                'installer.ChargepointsSummary.s14',
                'Edit details'
              )}
        </pre>
      </Link>,
      <Link
        className="govuk-link"
        to="/"
        onClick={(e) => handleDeleteLink(e, index)}
      >
        {copyProvider.getCopy('installer.ChargepointsSummary.s10', 'Delete')}
      </Link>,
    ];

    const actionList = actionArray.map((action) => (
      <li style={{ listStyle: 'none' }}>{action}</li>
    ));

    tableRows.push({
      cells: [
        {
          children: index + 1,
          format: 'text',
        },
        {
          children: model,
          format: 'text',
        },
        {
          children: socket,
          format: 'text',
        },
        {
          children: serialNumber,
          format: 'text',
        },
        {
          children: photoList,
          format: 'text',
        },
        {
          children: actionList,
          format: 'text',
        },
      ],
    });
  });

  if (isSerialMissing && !hasSerialError) {
    setHasSerialError(true);
  }
  if (!isSerialMissing && hasSerialError) {
    setHasSerialError(false);
  }

  if (hasPhotoUploadError && !isPhotoMissing) {
    setHasPhotoUploadError(false);
  }
  if (!hasPhotoUploadError && isPhotoMissing) {
    setHasPhotoUploadError(true);
  }

  const serialNumberErrorMsg = copyProvider.getCopy(
    'installer.ChargepointsSummary.s16',
    'Click "Add details" and provide a serial number'
  );

  const photosErrorMsg = copyProvider.getCopy(
    'installer.ChargepointsSummary.s15',
    'Click "Add details" and attach a minimum of 2 photographs'
  );

  const handleButtonClick = () => {
    const showErrorSerialNumber = {
      children: serialNumberErrorMsg,
    };

    const showErrorPhotoMsg = {
      children: photosErrorMsg,
    };

    if (hasSerialError) {
      setErrorMsg(serialNumberErrorMsg);
      setErrorList([{ ...showErrorSerialNumber, href: '#chargepoint-table' }]);
    } else if (hasPhotoUploadError) {
      setErrorMsg(photosErrorMsg);
      setErrorList([{ ...showErrorPhotoMsg, href: '#chargepoint-table' }]);
    } else if (!errorMsg) {
      setErrorMsg(null);
      const installedSockets = individualChargepointDetailsRedux
        .map((cp) => cp.socket)
        .reduce((acc, cp) => cp + acc);

      dispatch(setNumberOfChargepointsSocketsInstalled(installedSockets));
      dispatch(
        setNumberOfChargepointsInstalled(
          individualChargepointDetailsRedux.length
        )
      );
      dispatch(setSocketAllocationPage('chargepointSummary'));
      saveChargePoints({
        id: applicationId,
        chargePointData: individualChargepointDetailsRedux,
        totalSocketsInstalled: installedSockets,
        customerNominatedChargepointSockets:
          customerNominatedChargepointSocketsRedux,
        totalChargePointsInstalled: individualChargepointDetailsRedux.length,
      })
        .then((response) => {
          if (
            response.data.success &&
            response.data.status === 'Exact allocation'
          ) {
            history.push('/installer/installation-cost');
          } else if (
            response.data.success &&
            response.data.status === 'Under allocation'
          ) {
            history.push('/installer/socket-under-allocation');
          } else if (
            !response.data.success &&
            response.data.status === 'Over allocation'
          ) {
            history.push('/installer/socket-over-allocation');
          }
        })
        .catch(() => {
          // TODO: set error message
        });
    }
  };

  const pageModel = (
    <>
      {errorList.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <h1 className="govuk-heading-l">
        {copyProvider.getCopy(
          'installer.ChargepointsSummary.s1',
          'Add details for each chargepoint installed'
        )}
      </h1>
      <p className="govuk-body govuk-!-margin-top-7 govuk-!-margin-bottom-7">
        {copyProvider.getCopy(
          'installer.ChargepointsSummary.s2',
          '“Provide serial number and the photographs for chargepoint model installed.'
        )}
      </p>
      <ErrorMessage visuallyHiddenText="Error">{errorMsg || null}</ErrorMessage>
      <div className="govuk-table">
        <Table
          className="govuk-!-margin-0"
          id="chargepoint-table"
          head={[
            {
              children: copyProvider.getCopy(
                'installer.ChargepointsSummary.s3',
                'Chargepoint'
              ),
            },
            {
              children: copyProvider.getCopy(
                'installer.ChargepointsSummary.s4',
                'Model'
              ),
              format: 'text',
            },
            {
              children: copyProvider.getCopy(
                'installer.ChargepointsSummary.s5',
                'Sockets'
              ),
              format: 'text',
            },
            {
              children: copyProvider.getCopy(
                'installer.ChargepointsSummary.s6',
                'Serial number'
              ),
              format: 'text',
            },
            {
              children: copyProvider.getCopy(
                'installer.ChargepointsSummary.s7',
                'Photographs'
              ),
              format: 'text',
            },
            {
              children: copyProvider.getCopy(
                'installer.ChargepointsSummary.s8',
                'Action'
              ),
              format: 'text',
            },
            {
              children: '',
              format: 'text',
            },
          ]}
          rows={tableRows.map((row) => row)}
        />
      </div>
      <div className="govuk-!-margin-top-9">
        <Button
          className="govuk-button--secondary"
          onClick={() => {
            history.push('/installer/additional-chargepoints');
          }}
        >
          {copyProvider.getCopy(
            'installer.ChargepointsSummary.s11',
            'Add chargepoint'
          )}
        </Button>
      </div>
      <div className="govuk-button-group govuk-!-margin-top-2">
        <Button onClick={handleButtonClick}>
          {' '}
          {copyProvider.getCopy(
            'installer.ChargepointsSummary.s12',
            'Save and continue'
          )}
        </Button>
        <Link className="govuk-link" to={{}} onClick={handleLink}>
          {copyProvider.getCopy(
            'installer.ChargepointsSummary.s13',
            'Save and come back later'
          )}
        </Link>
      </div>
    </>
  );
  return pageModel;
};

export default ChargepointsSummary;
